import React, { useState, useEffect } from "react";
import { updateUserSettings } from "../utils/firestoreService";
import { getDocumentsCreated, getDocumentsCreatedByDay, getAllCompanies, getActiveUsers, getTotalUsers, getStreamingStats, getActiveUsersByDay, getSessionsByDay, getTotalRecordingMinutes, getDocumentsExported, getDocumentsExportedByDay, getFailedJobs, getLoginFailures, getDocumentsCreatedByHour, getActiveUsersByHour, getSessionsByHour, getDocumentsExportedByHour, getLivePresence, getAdminUsageSummary, clearStatsCache } from "../utils/statsService";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import LinearProgress from "@mui/material/LinearProgress";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import IconButton from "@mui/material/IconButton";
import Collapse from "@mui/material/Collapse";
import { FileText, Users, Mic, Clock, UserPlus, Download, AlertTriangle, ChevronDown, ChevronRight, Activity, RefreshCw } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { sortByDisplayLabel } from './sortByDisplayLabel';
import SearchableAdminSelect from './components/SearchableAdminSelect';

// Use explicit colors since CSS variables don't work in SVG
const CHART_COLOR = '#1976d2';

const AdminStats = ({ adminPrefs, setAdminPrefs, userId }) => {
  const [stats, setStats] = useState({ patientCount: null, documentCount: null, sharedPatientCount: null });
  const [isLoading, setIsLoading] = useState(true);
  const [period, setPeriod] = useState('week');
  const [companyId, setCompanyId] = useState('');
  const [companies, setCompanies] = useState([]);
  const [docsCreated, setDocsCreated] = useState(0);
  const [docsCreatedByDay, setDocsCreatedByDay] = useState([]);
  const [documentInsights, setDocumentInsights] = useState({
    categoryBreakdown: [],
    typeBreakdown: [],
    documentingUsers: 0,
    patientsDocumented: 0,
  });
  const [activeUsers, setActiveUsers] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [streamingStats, setStreamingStats] = useState({ avgDurationMin: 0, sessionCount: 0 });
  const [usersByDay, setUsersByDay] = useState([]);
  const [sessionsByDay, setSessionsByDay] = useState([]);
  const [totalRecordingMinutes, setTotalRecordingMinutes] = useState(0);
  const [patientsCreated, setPatientsCreated] = useState(0);
  const [patientsCreatedByDay, setPatientsCreatedByDay] = useState([]);
  const [docsExported, setDocsExported] = useState(0);
  const [docsExportedByDay, setDocsExportedByDay] = useState([]);
  // Hourly data for Today live view
  const [docsCreatedByHour, setDocsCreatedByHour] = useState([]);
  const [usersByHour, setUsersByHour] = useState([]);
  const [sessionsByHour, setSessionsByHour] = useState([]);
  const [patientsCreatedByHour, setPatientsCreatedByHour] = useState([]);
  const [docsExportedByHour, setDocsExportedByHour] = useState([]);
  // Live (last 5 minutes) recently-active users + currently streaming
  const [livePresence, setLivePresence] = useState(null);
  const [failedJobs, setFailedJobs] = useState({ total: 0, recent: [] });
  const [failedJobsError, setFailedJobsError] = useState(false);
  const [usageSummaryError, setUsageSummaryError] = useState(false);
  const [loginFailures, setLoginFailures] = useState([]);
  const [selectedChart, setSelectedChart] = useState('documents');
  const [statsLoading, setStatsLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  
  // Section collapse state
  const [cardsExpanded, setCardsExpanded] = useState(true);
  const [chartExpanded, setChartExpanded] = useState(true);
  const [errorsExpanded, setErrorsExpanded] = useState(true);

  // Sync state with adminPrefs on initial load only
  useEffect(() => {
    if (adminPrefs && !prefsLoaded) {
      if (adminPrefs.statsPeriod) setPeriod(adminPrefs.statsPeriod);
      if (adminPrefs.statsCompanyId !== undefined) setCompanyId(adminPrefs.statsCompanyId);
      if (adminPrefs.statsSelectedChart) setSelectedChart(adminPrefs.statsSelectedChart);
      // Section collapse states
      if (adminPrefs.statsCardsExpanded !== undefined) setCardsExpanded(adminPrefs.statsCardsExpanded);
      if (adminPrefs.statsChartExpanded !== undefined) setChartExpanded(adminPrefs.statsChartExpanded);
      if (adminPrefs.statsErrorsExpanded !== undefined) setErrorsExpanded(adminPrefs.statsErrorsExpanded);
      setPrefsLoaded(true);
    }
  }, [adminPrefs, prefsLoaded]);

  // Helper to save admin preferences
  const saveAdminPref = (key, value) => {
    if (userId && setAdminPrefs) {
      const updatedPrefs = { ...adminPrefs, [key]: value };
      setAdminPrefs(updatedPrefs);
      updateUserSettings(userId, { adminPreferences: updatedPrefs });
    }
  };

  // Section toggle helpers
  const toggleCards = () => {
    const newValue = !cardsExpanded;
    setCardsExpanded(newValue);
    saveAdminPref('statsCardsExpanded', newValue);
  };
  
  const toggleChart = () => {
    const newValue = !chartExpanded;
    setChartExpanded(newValue);
    saveAdminPref('statsChartExpanded', newValue);
  };
  
  const toggleErrors = () => {
    const newValue = !errorsExpanded;
    setErrorsExpanded(newValue);
    saveAdminPref('statsErrorsExpanded', newValue);
  };

  // Fetch companies for dropdown
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const data = await getAllCompanies();
        setCompanies(sortByDisplayLabel(data || [], company => company.name || company.id));
      } catch (error) {
        console.error("Error fetching companies:", error);
      }
    };
    fetchCompanies();
  }, []);

  // Fetch period-based stats when period or company changes
  useEffect(() => {
    let cancelled = false;
    const fetchPeriodStats = async () => {
      setStatsLoading(true);
      try {
        const [created, byDay, active, total, streaming, usersDay, sessionsDay, recordingMins, usageSummaryResult, exported, exportedDay, failed, logins] = await Promise.all([
          getDocumentsCreated(period, companyId || null),
          getDocumentsCreatedByDay(period, companyId || null),
          getActiveUsers(period, companyId || null),
          getTotalUsers(companyId || null),
          getStreamingStats(period, companyId || null),
          getActiveUsersByDay(period, companyId || null),
          getSessionsByDay(period, companyId || null),
          getTotalRecordingMinutes(period, companyId || null),
          getAdminUsageSummary(period, companyId || null)
            .then((data) => ({ data, failed: false }))
            .catch((error) => {
              console.error("Error fetching usage summary:", error);
              return { data: null, failed: true };
            }),
          getDocumentsExported(period, companyId || null),
          getDocumentsExportedByDay(period, companyId || null),
          getFailedJobs(period, companyId || null)
            .then((data) => ({ data, failed: false }))
            .catch(() => ({ data: { total: 0, recent: [] }, failed: true })),
          getLoginFailures(period),
        ]);
        if (cancelled) return;
        setDocsCreated(created);
        setDocsCreatedByDay(byDay);
        setActiveUsers(active);
        setTotalUsers(total);
        setStreamingStats(streaming);
        setUsersByDay(usersDay);
        setSessionsByDay(sessionsDay);
        setTotalRecordingMinutes(recordingMins);
        const usageSummary = usageSummaryResult.data;
        setUsageSummaryError(usageSummaryResult.failed);
        if (usageSummary) {
          setStats({
            patientCount: usageSummary.patientCount,
            documentCount: usageSummary.documentCount,
            sharedPatientCount: usageSummary.sharedPatientCount,
          });
          setPatientsCreated(usageSummary.patientsCreated);
          setPatientsCreatedByDay(usageSummary.patientsCreatedByDay);
          setDocsCreated(usageSummary.documentsCreated);
          setDocsCreatedByDay(usageSummary.documentsCreatedByDay);
          setDocumentInsights({
            categoryBreakdown: usageSummary.documentCategoryBreakdown,
            typeBreakdown: usageSummary.documentTypeBreakdown,
            documentingUsers: usageSummary.documentingUsers,
            patientsDocumented: usageSummary.patientsDocumented,
          });
        } else {
          setStats({ patientCount: null, documentCount: null, sharedPatientCount: null });
          setPatientsCreated(0);
          setPatientsCreatedByDay([]);
          setPatientsCreatedByHour([]);
          setDocumentInsights({
            categoryBreakdown: [],
            typeBreakdown: [],
            documentingUsers: 0,
            patientsDocumented: 0,
          });
        }
        setDocsExported(exported);
        setDocsExportedByDay(exportedDay);
        setFailedJobs(failed.data);
        setFailedJobsError(failed.failed);
        setLoginFailures(logins);

        // Hourly data for the Today live view (last 24 hours)
        if (period === 'today') {
          const [docsHour, usersHour, sessionsHour, exportsHour] = await Promise.all([
            getDocumentsCreatedByHour(companyId || null),
            getActiveUsersByHour(companyId || null),
            getSessionsByHour(companyId || null),
            getDocumentsExportedByHour(companyId || null),
          ]);
          if (cancelled) return;
          setDocsCreatedByHour(usageSummary?.documentsCreatedByHour || docsHour);
          setUsersByHour(usersHour);
          setSessionsByHour(sessionsHour);
          if (usageSummary) setPatientsCreatedByHour(usageSummary.patientsCreatedByHour);
          setDocsExportedByHour(exportsHour);
        }
      } catch (error) {
        if (!cancelled) console.error("Error fetching period stats:", error);
      }
      if (!cancelled) {
        setStatsLoading(false);
        setIsLoading(false);
      }
    };
    fetchPeriodStats();
    return () => {
      cancelled = true;
    };
  }, [period, companyId, refreshKey]);

  // Live "recently active users" counter — polls every 30s while on Today view
  useEffect(() => {
    if (period !== 'today') {
      setLivePresence(null);
      return undefined;
    }
    let cancelled = false;
    const fetchLive = async () => {
      try {
        const presence = await getLivePresence(companyId || null, 5);
        if (!cancelled) setLivePresence(presence);
      } catch (err) {
        console.error('Error fetching live presence:', err);
      }
    };
    fetchLive();
    const intervalId = setInterval(fetchLive, 30 * 1000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [period, companyId]);

  if (isLoading) {
    return (
      <div className="standard-container">
        <Box sx={{ width: '100%', mt: 4 }}>
          <LinearProgress />
        </Box>
      </div>
    );
  }

  return (
    <div className="standard-container">
      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, mt: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <SearchableAdminSelect
            label="Company"
            value={companyId}
            sx={{ minWidth: 190 }}
            options={[{ value: '', label: 'All Companies' }, ...companies.map(company => ({
              value: company.id,
              label: company.name || company.id,
            }))]}
            onChange={selectedId => {
              setCompanyId(selectedId);
              saveAdminPref('statsCompanyId', selectedId);
            }}
          />

        <ToggleButtonGroup
          value={period}
          exclusive
          onChange={(e, newPeriod) => {
            if (newPeriod) {
              setPeriod(newPeriod);
              saveAdminPref('statsPeriod', newPeriod);
            }
          }}
          size="small"
        >
          <ToggleButton value="today">Today</ToggleButton>
          <ToggleButton value="week">Week</ToggleButton>
          <ToggleButton value="month">Month</ToggleButton>
        </ToggleButtonGroup>

        <IconButton
          aria-label="Refresh statistics"
          title="Refresh statistics"
          onClick={() => {
            clearStatsCache();
            setRefreshKey((value) => value + 1);
          }}
          disabled={statsLoading}
          size="small"
        >
          <RefreshCw size={18} />
        </IconButton>
      </Box>

      {/* Company filter notice */}
      {companyId && !statsLoading && !usageSummaryError && docsCreated === 0 && activeUsers === 0 && streamingStats.sessionCount === 0 && patientsCreated === 0 && docsExported === 0 && (
        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'warning.light', borderRadius: 1, opacity: 0.9 }}>
          <Typography variant="body2" color="warning.dark">
            No activity found for this company in the selected period. Some older logs may not have company data.
          </Typography>
        </Box>
      )}

      {/* Stats Cards - Collapsible */}
      <Box sx={{ mb: 2 }}>
        <Box 
          onClick={toggleCards}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 1, '&:hover': { opacity: 0.8 } }}
        >
          <IconButton size="small" sx={{ p: 0, mr: 0.5 }}>
            {cardsExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          </IconButton>
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>Summary Cards</Typography>
        </Box>
        <Collapse in={cardsExpanded}>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {period === 'today' && (
          <Paper
            sx={{
              p: 3, minWidth: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
              border: '2px solid',
              borderColor: 'success.main',
            }}
          >
            <Activity size={32} color="#2e7d32" />
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
              {livePresence === null ? '...' : livePresence.total}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Active Now
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ borderTop: '1px solid #eee', pt: 1, mt: 1, width: '100%', textAlign: 'center' }}>
              {livePresence === null
                ? 'last 5 min · refreshes every 30s'
                : `${livePresence.streaming} streaming · last 5 min`}
            </Typography>
          </Paper>
        )}
        <Paper 
          onClick={() => {
            setSelectedChart('documents');
            saveAdminPref('statsSelectedChart', 'documents');
          }}
          sx={{ 
            p: 3, minWidth: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
            cursor: 'pointer',
            border: '2px solid',
            borderColor: selectedChart === 'documents' ? 'var(--color-primary)' : 'transparent',
            transition: 'border-color 0.2s',
            '&:hover': { 
              borderColor: selectedChart === 'documents' ? 'var(--color-primary)' : 'var(--color-primary-light, #ccc)' 
            }
          }}
        >
          <FileText size={32} color="var(--color-primary)" />
          <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
            {statsLoading ? "..." : docsCreated}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Documents Created
          </Typography>
          <Box sx={{ borderTop: '1px solid #eee', pt: 1, mt: 1, width: '100%', textAlign: 'center' }}>
            {usageSummaryError ? (
              <Typography variant="caption" color="text.secondary">
                Breakdown unavailable
              </Typography>
            ) : (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.6 }}>
                {documentInsights.categoryBreakdown.map((bucket, index) => (
                  <React.Fragment key={bucket.key}>
                    {index > 0 && ' · '}
                    {bucket.label} {bucket.count}
                  </React.Fragment>
                ))}
              </Typography>
            )}
            {!companyId && (
              <Typography variant="caption" color="text.secondary" component="div">
                {stats.documentCount !== null ? `${stats.documentCount} total` : "- total"}
              </Typography>
            )}
          </Box>
        </Paper>

        <Paper 
          onClick={() => {
            setSelectedChart('users');
            saveAdminPref('statsSelectedChart', 'users');
          }}
          sx={{ 
            p: 3, minWidth: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
            cursor: 'pointer',
            border: '2px solid',
            borderColor: selectedChart === 'users' ? 'var(--color-primary)' : 'transparent',
            transition: 'border-color 0.2s',
            '&:hover': { 
              borderColor: selectedChart === 'users' ? 'var(--color-primary)' : 'var(--color-primary-light, #ccc)' 
            }
          }}
        >
          <Users size={32} color="var(--color-primary)" />
          <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
            {statsLoading ? "..." : activeUsers}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Active Users
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ borderTop: '1px solid #eee', pt: 1, mt: 1, width: '100%', textAlign: 'center' }}>
            {totalUsers} total
          </Typography>
        </Paper>

        <Paper 
          onClick={() => {
            setSelectedChart('sessions');
            saveAdminPref('statsSelectedChart', 'sessions');
          }}
          sx={{ 
            p: 3, minWidth: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
            cursor: 'pointer',
            border: '2px solid',
            borderColor: selectedChart === 'sessions' ? 'var(--color-primary)' : 'transparent',
            transition: 'border-color 0.2s',
            '&:hover': { 
              borderColor: selectedChart === 'sessions' ? 'var(--color-primary)' : 'var(--color-primary-light, #ccc)' 
            }
          }}
        >
          <Mic size={32} color="var(--color-primary)" />
          <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
            {statsLoading ? "..." : `${streamingStats.avgDurationMin}`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Avg Session (min)
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ borderTop: '1px solid #eee', pt: 1, mt: 1, width: '100%', textAlign: 'center' }}>
            {streamingStats.sessionCount} sessions
          </Typography>
        </Paper>

        <Paper 
          onClick={() => {
            setSelectedChart('recording');
            saveAdminPref('statsSelectedChart', 'recording');
          }}
          sx={{ 
            p: 3, minWidth: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
            cursor: 'pointer',
            border: '2px solid',
            borderColor: selectedChart === 'recording' ? 'var(--color-primary)' : 'transparent',
            transition: 'border-color 0.2s',
            '&:hover': { 
              borderColor: selectedChart === 'recording' ? 'var(--color-primary)' : 'var(--color-primary-light, #ccc)' 
            }
          }}
        >
          <Clock size={32} color="var(--color-primary)" />
          <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
            {statsLoading ? "..." : (totalRecordingMinutes >= 60 
              ? `${Math.floor(totalRecordingMinutes / 60)}h ${Math.round(totalRecordingMinutes % 60)}m`
              : `${totalRecordingMinutes}m`)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total Recording
          </Typography>
        </Paper>

        <Paper 
          onClick={() => {
            setSelectedChart('patients');
            saveAdminPref('statsSelectedChart', 'patients');
          }}
          sx={{ 
            p: 3, minWidth: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
            cursor: 'pointer',
            border: '2px solid',
            borderColor: selectedChart === 'patients' ? 'var(--color-primary)' : 'transparent',
            transition: 'border-color 0.2s',
            '&:hover': { 
              borderColor: selectedChart === 'patients' ? 'var(--color-primary)' : 'var(--color-primary-light, #ccc)' 
            }
          }}
        >
          <UserPlus size={32} color="var(--color-primary)" />
          <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
            {statsLoading ? "..." : usageSummaryError ? "—" : patientsCreated}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Patients Created
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ borderTop: '1px solid #eee', pt: 1, mt: 1, width: '100%', textAlign: 'center' }}>
            {usageSummaryError
              ? "Temporarily unavailable"
              : stats.patientCount !== null ? `${stats.patientCount} total` : "- total"}
            {!usageSummaryError && stats.sharedPatientCount !== null && ` · ${stats.sharedPatientCount} shared`}
          </Typography>
        </Paper>

        <Paper 
          onClick={() => {
            setSelectedChart('exports');
            saveAdminPref('statsSelectedChart', 'exports');
          }}
          sx={{ 
            p: 3, minWidth: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
            cursor: 'pointer',
            border: '2px solid',
            borderColor: selectedChart === 'exports' ? 'var(--color-primary)' : 'transparent',
            transition: 'border-color 0.2s',
            '&:hover': { 
              borderColor: selectedChart === 'exports' ? 'var(--color-primary)' : 'var(--color-primary-light, #ccc)' 
            }
          }}
        >
          <Download size={32} color="var(--color-primary)" />
          <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
            {statsLoading ? "..." : docsExported}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Documents Exported
          </Typography>
        </Paper>
          </Box>
        </Collapse>
      </Box>

      {/* Dynamic Chart — Collapsible */}
      {!statsLoading && (
        <Box sx={{ mb: 2 }}>
          <Box 
            onClick={toggleChart}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 1, '&:hover': { opacity: 0.8 } }}
          >
            <IconButton size="small" sx={{ p: 0, mr: 0.5 }}>
              {chartExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </IconButton>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              {(() => {
                const rangeLabel = period === 'today' ? 'Last 24 Hours' : period === 'week' ? 'Last 7 Days' : 'Last 30 Days';
                if (selectedChart === 'documents') return `Documents Created (${rangeLabel})`;
                if (selectedChart === 'users') return `Active Users (${rangeLabel})`;
                if (selectedChart === 'sessions') return `Streaming Sessions (${rangeLabel})`;
                if (selectedChart === 'recording') return `Recording Sessions (${rangeLabel})`;
                if (selectedChart === 'patients') return `Patients Created (${rangeLabel})`;
                if (selectedChart === 'exports') return `Documents Exported (${rangeLabel})`;
                return '';
              })()}
            </Typography>
          </Box>
          <Collapse in={chartExpanded}>
            <Paper sx={{ p: 3 }}>
          {(() => {
            const isHourly = period === 'today';
            const chartData = isHourly
              ? (selectedChart === 'documents' ? docsCreatedByHour :
                 selectedChart === 'users' ? usersByHour :
                 selectedChart === 'sessions' ? sessionsByHour :
                 selectedChart === 'recording' ? sessionsByHour :
                 selectedChart === 'patients' ? patientsCreatedByHour :
                 selectedChart === 'exports' ? docsExportedByHour : docsCreatedByHour)
              : (selectedChart === 'documents' ? docsCreatedByDay :
                 selectedChart === 'users' ? usersByDay :
                 selectedChart === 'sessions' ? sessionsByDay :
                 selectedChart === 'recording' ? sessionsByDay :
                 selectedChart === 'patients' ? patientsCreatedByDay :
                 selectedChart === 'exports' ? docsExportedByDay : docsCreatedByDay);
            if (selectedChart === 'patients' && usageSummaryError) {
              return (
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary">Patient statistics are temporarily unavailable</Typography>
                </Box>
              );
            }

            const hasData = chartData.some(d => d.count > 0);
            
            if (!hasData) {
              return (
                <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography color="text.secondary">No data for this period</Typography>
                </Box>
              );
            }
            
            return (
              <>
                <Box sx={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartData}>
                      <CartesianGrid stroke="#e0e0e0" />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 12 }}
                        interval={period === 'month' ? 4 : period === 'today' ? 2 : 0}
                      />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="count"
                        stroke={CHART_COLOR}
                        strokeWidth={2}
                        dot={{ fill: CHART_COLOR }}
                        name={
                          selectedChart === 'documents' ? 'Documents' :
                          selectedChart === 'users' ? 'Users' :
                          selectedChart === 'sessions' ? 'Sessions' :
                          selectedChart === 'recording' ? 'Sessions' :
                          selectedChart === 'patients' ? 'Patients' :
                          selectedChart === 'exports' ? 'Exports' : 'Count'
                        }
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </>
            );
          })()}
            </Paper>
          </Collapse>
        </Box>
      )}

      {/* Keep document activity visible regardless of chart selection/collapse. */}
      <Paper component="section" aria-labelledby="document-type-breakdown" sx={{ p: 3, mb: 2 }}>
        <Typography id="document-type-breakdown" variant="subtitle1" sx={{ fontWeight: 500, mb: 1 }}>
          Document Type Breakdown
        </Typography>
        {statsLoading ? (
          <Typography role="status" color="text.secondary">Loading document activity...</Typography>
        ) : usageSummaryError ? (
          <Typography role="alert" color="text.secondary">
            Document activity is temporarily unavailable. Refresh statistics to try again.
          </Typography>
        ) : documentInsights.typeBreakdown.length === 0 ? (
          <Typography color="text.secondary">No documents created for this period and company filter.</Typography>
        ) : (
          <>
            <Box component="table" aria-label="Document activity by type" sx={{ width: '100%', textAlign: 'left', '& th, & td': { py: 0.5, pr: 2 }, '& th': { fontWeight: 500 } }}>
              <thead>
                <tr><th scope="col">Document type</th><th scope="col">Documents</th><th scope="col">Share</th></tr>
              </thead>
              <tbody>
                {documentInsights.typeBreakdown.map((type) => (
                  <tr key={`${type.typeId || type.name}-${type.category}`}>
                    <td>{type.name}</td>
                    <td>{type.count}</td>
                    <td>{docsCreated > 0 ? `${Math.round((type.count / docsCreated) * 100)}%` : '0%'}</td>
                  </tr>
                ))}
              </tbody>
            </Box>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5 }}>
              {documentInsights.documentingUsers} of {totalUsers} users created documents · {documentInsights.patientsDocumented} patients documented
            </Typography>
          </>
        )}
      </Paper>

      {/* Error Summary Section - Collapsible */}
      {!statsLoading && (failedJobs.total > 0 || failedJobsError || loginFailures.length > 0) && (
        <Box sx={{ mb: 2 }}>
          <Box 
            onClick={toggleErrors}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 1, '&:hover': { opacity: 0.8 } }}
          >
            <IconButton size="small" sx={{ p: 0, mr: 0.5 }}>
              {errorsExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </IconButton>
            <AlertTriangle size={18} color="var(--color-error, #d32f2f)" style={{ marginRight: 6 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              Errors & Failures ({period === 'today' ? 'Today' : period === 'week' ? 'Last 7 Days' : 'Last 30 Days'})
            </Typography>
          </Box>
          <Collapse in={errorsExpanded}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {failedJobsError && (
              <Box sx={{ flex: 1, minWidth: 280, p: 1.5, bgcolor: '#fff3e0', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="warning.dark">
                  Processing failure summary is temporarily unavailable.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Other statistics are unaffected.
                </Typography>
              </Box>
            )}
            {/* Failed Processing Jobs */}
            {failedJobs.total > 0 && (
              <Box sx={{ flex: 1, minWidth: 280 }}>
                <Typography variant="subtitle2" color="error" sx={{ mb: 1 }}>
                  Failed Processing Jobs ({failedJobs.total})
                </Typography>
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {failedJobs.recent.map((job) => (
                    <Box key={job.id} sx={{ p: 1.5, mb: 1, bgcolor: '#ffebee', borderRadius: 1, borderLeft: '3px solid #d32f2f' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {job.errorCode}
                        </Typography>
                        {job.sourceType && (
                          <Typography variant="caption" sx={{ bgcolor: '#fff', px: 0.75, py: 0.25, borderRadius: 0.5, color: '#666' }}>
                            {job.sourceType}
                          </Typography>
                        )}
                      </Box>
                      {job.message && (
                        <Typography variant="caption" sx={{ display: 'block', color: '#b71c1c', mt: 0.5 }}>
                          {job.message}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Job {job.id.slice(-8)}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {job.createdAt?.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) || 'Unknown time'}
                        </Typography>
                        {job.retryCount > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            {job.retryCount} retries
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                  {failedJobs.total > failedJobs.recent.length && (
                    <Typography variant="caption" color="text.secondary">
                      + {failedJobs.total - failedJobs.recent.length} more
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            {/* Login Failures */}
            {loginFailures.length > 0 && (
              <Box sx={{ flex: 1, minWidth: 280 }}>
                <Typography variant="subtitle2" color="warning.dark" sx={{ mb: 1 }}>
                  Login Failures ({loginFailures.length})
                </Typography>
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {loginFailures.slice(0, 10).map((failure) => {
                    // Friendly error messages
                    const errorMessages = {
                      'auth/too-many-requests': 'Account locked (too many attempts)',
                      'auth/wrong-password': 'Wrong password',
                      'auth/user-not-found': 'User not found',
                      'auth/invalid-email': 'Invalid email',
                      'auth/user-disabled': 'Account disabled',
                      'auth/invalid-credential': 'Invalid credentials',
                    };
                    const friendlyError = errorMessages[failure.errorCode] || failure.errorCode;
                    const isLockout = failure.errorCode === 'auth/too-many-requests';
                    
                    return (
                      <Box 
                        key={failure.id} 
                        sx={{ 
                          p: 1.5, mb: 1, 
                          bgcolor: isLockout ? '#ffebee' : '#fff8e1', 
                          borderRadius: 1,
                          borderLeft: `3px solid ${isLockout ? '#d32f2f' : '#f57c00'}`
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {failure.userEmail}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: isLockout ? '#b71c1c' : '#e65100', mt: 0.5 }}>
                          {friendlyError}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {failure.timestamp?.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) || 'Unknown time'}
                        </Typography>
                      </Box>
                    );
                  })}
                  {loginFailures.length > 10 && (
                    <Typography variant="caption" color="text.secondary">
                      + {loginFailures.length - 10} more
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
              </Box>
            </Paper>
          </Collapse>
        </Box>
      )}
    </div>
  );
};

export default AdminStats;
